// src/components/feedback/FeedbackCongratulations.tsx
import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaCheckCircle, FaThumbsUp } from 'react-icons/fa';

interface FeedbackCongratulationsProps {
  show: boolean;
  onClose: () => void;
  duration?: number; // Duration in milliseconds (default 5000ms = 5 seconds)
}

const FeedbackCongratulations: React.FC<FeedbackCongratulationsProps> = ({ 
  show, 
  onClose,
  duration = 5000 
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [show, duration, onClose]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ 
            opacity: 1, 
            scale: 1, 
            y: 0,
            transition: { 
              type: "spring", 
              stiffness: 200, 
              damping: 15 
            }
          }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 pointer-events-none"
        >
          <motion.div
            className="relative text-center p-8 rounded-2xl max-w-sm mx-auto bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            style={{
              pointerEvents: 'auto',
            }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ 
                scale: [0, 1.2, 1],
                rotate: [0, 360, 0]
              }}
              transition={{ 
                duration: 0.8,
                times: [0, 0.6, 1],
                ease: "easeInOut"
              }}
              className="inline-block mb-4"
            >
              <FaCheckCircle className="text-green-500 text-5xl" />
            </motion.div>
            
            <motion.h3
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-2xl font-bold text-green-800 mb-2"
            >
              🎉 {t('feedback.congratulations', 'Congratulations!')}
            </motion.h3>
            
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-green-700 text-base leading-relaxed mb-4"
            >
              {t('feedback.success')}
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="flex justify-center items-center gap-2"
            >
              <FaThumbsUp className="text-green-600" />
              <span className="text-green-600 font-medium">
                {t('feedback.appreciateYou', 'We appreciate you!')}
              </span>
            </motion.div>
            
            {/* Sparkle animation effect */}
            <motion.div
              className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {[...Array(12)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 bg-yellow-400 rounded-full"
                  style={{
                    left: `${20 + Math.random() * 60}%`,
                    top: `${20 + Math.random() * 60}%`,
                  }}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{
                    scale: [0, 1, 0],
                    opacity: [0, 1, 0],
                    y: [-20, -40, -60],
                  }}
                  transition={{
                    duration: 2,
                    delay: i * 0.08,
                    ease: "easeOut",
                  }}
                />
              ))}
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FeedbackCongratulations;
